import { useMemo, useRef, useState, useEffect } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { LuClock } from "react-icons/lu";
import { getRefundAdminGuidance } from "../../../../data/flight/order/getRefundAdminGuidance";

const TONE_CLASS = {
  action: "bg-[#DBEAFE] border-primary1",
  wait: "bg-amber-50 border-amber-300",
  success: "bg-green-50 border-green-300",
  danger: "bg-red-50 border-red-300",
  info: "bg-gray-50 border-gray-200",
};

const TITLE_CLASS = {
  action: "text-gray-900",
  wait: "text-amber-900",
  success: "text-green-800",
  danger: "text-red-700",
  info: "text-gray-900",
};

function formatCountdown(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h:${String(m).padStart(2, "0")}m`;
  return `${m}m`;
}

export default function RefundNextStepCard({
  data,
  loading = {},
  onAction,
}) {
  const deadline = useMemo(() => {
    const raw = data?.requestExpirationTime;
    const d = raw ? new Date(raw) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  }, [data?.requestExpirationTime]);

  const calcSecondsLeft = () =>
    deadline
      ? Math.max(0, Math.floor((deadline.getTime() - Date.now()) / 1000))
      : 0;

  const initialWindowRef = useRef(calcSecondsLeft());
  const [timeLeft, setTimeLeft] = useState(initialWindowRef.current);

  // Recompute when the countdown hits 0 so soft-expired RefundQuoted
  // flips to quote_expired (status label + Enter new quote).
  const guidance = useMemo(
    () => getRefundAdminGuidance(data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, timeLeft],
  );
  const showExpiry = ["awaiting_agent", "quote_expired"].includes(
    guidance.stage,
  );
  const expired =
    guidance.stage === "quote_expired" ||
    (showExpiry && Boolean(deadline) && timeLeft <= 0);

  useEffect(() => {
    initialWindowRef.current = calcSecondsLeft();
    setTimeLeft(initialWindowRef.current);
  }, [deadline]);

  useEffect(() => {
    if (!showExpiry || expired) return;
    const id = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [showExpiry, expired]);

  const actionBusy = Object.values(loading || {}).some(Boolean);

  return (
    <div
      className={`w-full rounded-xl mt-4 flex flex-col gap-3 px-4 py-4 border ${
        TONE_CLASS[guidance.tone] || TONE_CLASS.info
      }`}
    >
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <FaExclamationTriangle
            className={`mt-1 flex-shrink-0 ${
              guidance.tone === "danger"
                ? "text-red-500"
                : guidance.tone === "success"
                  ? "text-green-600"
                  : "text-yellow-500"
            }`}
          />
          <div className="text-sm text-gray-800 flex flex-col gap-1">
            <span
              className={`font-semibold text-base ${
                TITLE_CLASS[guidance.tone] || TITLE_CLASS.info
              }`}
            >
              {guidance.title}
            </span>
            <span>{guidance.description}</span>
            {guidance.steps?.length ? (
              <div className="mt-2">
                <div className="font-semibold text-gray-900">Do this</div>
                <ol className="mt-1 list-decimal pl-5 space-y-1">
                  {guidance.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
            ) : null}
            {data?.reason ? (
              <span className="text-xs text-gray-600">
                Request reason: {data.reason}
                {data?.refundType ? ` · ${data.refundType}` : ""}
              </span>
            ) : null}
          </div>
        </div>

        {showExpiry && deadline ? (
          <div className="flex items-center gap-1 text-gray-800 text-sm font-medium shrink-0">
            <LuClock className={expired ? "text-red-500" : "text-blue-500"} />
            <span className="text-lg">
              {expired ? "Expired" : formatCountdown(timeLeft)}
            </span>
          </div>
        ) : null}
      </div>

      {guidance.primary?.length || guidance.secondary?.length ? (
        <div className="flex flex-wrap items-center gap-2 pl-7">
          {guidance.primary.map((action) => (
            <button
              key={action.key}
              type="button"
              disabled={actionBusy}
              onClick={() => onAction?.(action.key)}
              className="h-10 px-4 rounded-md bg-primary1 text-white text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading?.[action.key] ? (
                <span className="buttonLoader inline-block" />
              ) : (
                action.label
              )}
            </button>
          ))}
          {guidance.secondary.map((action) => (
            <button
              key={action.key}
              type="button"
              disabled={actionBusy}
              onClick={() => onAction?.(action.key)}
              className="h-10 px-4 rounded-md border border-gray-400 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {loading?.[action.key] ? (
                <span className="buttonLoader inline-block" />
              ) : (
                action.label
              )}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
